import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Globe2, UploadCloud, History, FileSpreadsheet, CalendarRange, ExternalLink,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Site-specific platforms that publish wide-format monthly history exports.
const HISTORICAL_PLATFORMS = [
  { key: "awstats", label: "AWStats (monthly totals)" },
  { key: "google_analytics", label: "Google Analytics (monthly totals)" },
  { key: "google_search_console", label: "Search Console (monthly totals)" },
  { key: "bing_analytics", label: "Bing Webmaster (monthly totals)" },
];

export default function HistoricalStatsPage() {
  const { clientId } = useParams();
  const [bus, setBus] = useState([]);
  // {[bu_id]: {[platform]: {count, earliest, latest, latest_visits}}}
  const [coverage, setCoverage] = useState({});

  const load = useCallback(async () => {
    const { data: bizUnits } = await api.get(`/clients/${clientId}/business-units`);
    bizUnits.sort(
      (a, b) => (a.priority || 5) - (b.priority || 5) || a.name.localeCompare(b.name)
    );
    setBus(bizUnits);

    // Build coverage map from /uploads list, filtering by platform
    const cov = {};
    await Promise.all(
      bizUnits.map(async (bu) => {
        cov[bu.business_unit_id] = {};
        await Promise.all(
          HISTORICAL_PLATFORMS.map(async ({ key }) => {
            try {
              const { data: ups } = await api.get(
                `/clients/${clientId}/uploads?business_unit_id=${bu.business_unit_id}&platform=${key}`
              );
              const historical = (ups || []).filter((u) => u.historical_import);
              if (!historical.length) {
                cov[bu.business_unit_id][key] = null;
                return;
              }
              const months = historical
                .map((u) => u.period_month)
                .sort();
              // pull most recent visits from latest historical upload
              const latestUp = historical
                .slice()
                .sort((a, b) => b.period_month.localeCompare(a.period_month))[0];
              const tot = latestUp?.summary?.totals || {};
              let latestVisits = null;
              for (const [k, v] of Object.entries(tot)) {
                const kl = String(k).toLowerCase();
                if (kl.includes("number of visit") || kl === "visits") {
                  latestVisits = v; break;
                }
              }
              if (latestVisits == null) {
                for (const [k, v] of Object.entries(tot)) {
                  const kl = String(k).toLowerCase();
                  if (kl.includes("visit") || kl.includes("session")) {
                    latestVisits = v; break;
                  }
                }
              }
              cov[bu.business_unit_id][key] = {
                count: historical.length,
                earliest: months[0],
                latest: months[months.length - 1],
                latest_visits: latestVisits,
              };
            } catch {
              cov[bu.business_unit_id][key] = null;
            }
          })
        );
      })
    );
    setCoverage(cov);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="historical-stats-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline flex items-center gap-1">
            <History className="h-3 w-3" /> Historical web traffic
          </div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Historical stats backfill
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Upload a single wide-format export (one row per month, with columns like
            Month / Unique visitors / Number of visits / Pages / Hits / Bandwidth)
            and we&apos;ll automatically back-fill one record per month — powering
            multi-year trend charts and month-over-month deltas in the PDF report.
          </p>
          <p className="text-xs text-zinc-500 mt-2">
            <strong>Tip:</strong> this is different from the per-day monthly uploads
            you do on the CSV Uploads tab. Use this page only for the monthly
            summary export (AWStats annual / GA4 monthly export / etc).
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {bus.map((bu) => (
          <PropertyRow
            key={bu.business_unit_id}
            bu={bu}
            coverage={coverage[bu.business_unit_id] || {}}
            onUploaded={load}
            clientId={clientId}
          />
        ))}
        {bus.length === 0 && (
          <Card className="rounded-sm border-dashed border-zinc-300">
            <CardContent className="p-10 text-center text-sm text-zinc-500">
              No properties yet. Add one on the Properties tab first.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function PropertyRow({ bu, coverage, onUploaded, clientId }) {
  const imgSrc = bu.og_image_url ? `${BACKEND}${bu.og_image_url}` : null;
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("awstats");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file || !platform) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("platform", platform);
      fd.append("business_unit_id", bu.business_unit_id);
      const { data } = await api.post(
        `/clients/${clientId}/uploads/historical`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `Back-filled ${data.imported_months} months for ${bu.name} (${data.earliest} → ${data.latest})`
      );
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setOpen(false);
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  // Total months covered across any platform
  const totalEntries = useMemo(
    () => Object.values(coverage || {}).reduce(
      (acc, v) => acc + (v?.count || 0), 0
    ),
    [coverage]
  );

  return (
    <Card className="rounded-sm border-zinc-200" data-testid={`historical-bu-row-${bu.business_unit_id}`}>
      <CardContent className="p-0">
        <div className="p-4 flex items-center gap-3">
          <div className="relative h-12 w-12 rounded-sm overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200">
            {imgSrc ? (
              <img src={imgSrc} alt={bu.name} className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="h-full w-full bg-zinc-900 text-white flex items-center justify-center">
                <Globe2 className="h-5 w-5" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-bold text-sm">{bu.name}</span>
              <Badge variant="outline" className="rounded-sm text-[10px]">
                P{bu.priority ?? 5}
              </Badge>
              {totalEntries === 0 ? (
                <Badge variant="outline" className="rounded-sm text-[10px] text-amber-600 border-amber-300 bg-amber-50">
                  No historical data
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-sm text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50">
                  {totalEntries} months on file
                </Badge>
              )}
            </div>
            <a href={bu.website_url} target="_blank" rel="noreferrer"
              className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-[#002FA7] mt-1">
              {bu.website_url} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <Button
            onClick={() => setOpen((o) => !o)}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid={`historical-upload-toggle-${bu.business_unit_id}`}
          >
            <UploadCloud className="h-4 w-4 mr-1.5" />
            {open ? "Cancel" : "Upload history"}
          </Button>
        </div>

        {/* Coverage breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-zinc-100">
          {HISTORICAL_PLATFORMS.map((p) => {
            const c = coverage?.[p.key];
            return (
              <div
                key={p.key}
                className="p-4 border-r border-zinc-100 last:border-r-0 odd:bg-zinc-50/50"
                data-testid={`historical-coverage-${bu.business_unit_id}-${p.key}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
                  {p.label}
                </div>
                {c ? (
                  <>
                    <div className="text-sm font-mono text-zinc-900">
                      <CalendarRange className="h-3.5 w-3.5 inline mr-1 text-zinc-400" />
                      {c.earliest} → {c.latest}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {c.count} months · latest visits{" "}
                      <span className="font-mono text-zinc-700">
                        {c.latest_visits != null
                          ? Number(c.latest_visits).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-zinc-400 italic">
                    Not uploaded yet
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {open && (
          <div className="p-5 border-t border-zinc-100 bg-[#F4F5FB]"
            data-testid={`historical-upload-form-${bu.business_unit_id}`}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="label-overline">Source</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="rounded-sm h-10 bg-white"
                    data-testid={`historical-platform-select-${bu.business_unit_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HISTORICAL_PLATFORMS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-overline">Wide-format file</Label>
                <Input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="rounded-sm h-10 bg-white"
                  data-testid={`historical-file-input-${bu.business_unit_id}`}
                />
              </div>
              <Button
                onClick={submit}
                disabled={busy || !file || !platform}
                className="rounded-sm h-10 bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid={`historical-submit-${bu.business_unit_id}`}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                {busy ? "Importing…" : "Backfill months"}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              Re-uploading the same file is safe — months that already exist will be
              replaced rather than duplicated.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
